/**
 * Same-version lobbies on the public site: a peer that sees another player on
 * a newer build checks that the newer build is deployed (`version.json`, see
 * farm.config.ts), then reloads onto it and rejoins the same lobby
 * (`rejoinRoom`). The decisions are pure, in `lib/version.ts`.
 */
import { useEffect, useMemo, useState } from 'react';
import { BUILD_ID } from '@/config';
import { newestPeerBuild, parseVersionFile, reloadUrl, updateAction } from '@/lib/version';

const ATTEMPT_KEY = 'territile.update.attempt';

/** How long to wait for `version.json` before giving up (then: 'notice'). */
const VERSION_TIMEOUT_MS = 5000;

/** The build we last reloaded for, this tab (also from `?v=`, in case storage is blocked). */
let reloadedFor: string | null = null;

function readAttempt(): string | null {
  try {
    return sessionStorage.getItem(ATTEMPT_KEY) ?? reloadedFor;
  } catch {
    return reloadedFor;
  }
}

function writeAttempt(build: string): void {
  reloadedFor = build;
  try {
    sessionStorage.setItem(ATTEMPT_KEY, build);
  } catch {
    // Private mode or blocked storage: `?v=` still marks the attempt.
  }
}

/**
 * The lobby to join at once when the page was loaded by an update reload
 * (`?room=…&v=…`), else null (a plain `?room=` is a shared link: `lib/share.ts`).
 * Takes `v` out of the address bar so a manual reload is an ordinary one.
 */
export function rejoinRoom(): string | null {
  const url = new URL(window.location.href);
  const room = url.searchParams.get('room')?.trim();
  const v = url.searchParams.get('v');
  if (v === null) return null;
  writeAttempt(v);
  url.searchParams.delete('v');
  window.history.replaceState(window.history.state, '', url);
  return room ? room : null;
}

async function deployedBuild(): Promise<string | null> {
  try {
    // The signal also covers reading the body, so a stalled response ends too.
    const res = await fetch('/version.json', {
      cache: 'no-store',
      signal: AbortSignal.timeout(VERSION_TIMEOUT_MS),
    });
    return res.ok ? parseVersionFile(await res.json()) : null;
  } catch {
    return null; // network error, timeout or bad JSON: 'notice'
  }
}

/**
 * 'checking' while fetching `version.json` for a newer build some peer runs
 * (the lobby stays usable), 'downloading' once we're reloading onto it,
 * 'notice' when we can't get it (none deployed here, `version.json` unreadable
 * within `VERSION_TIMEOUT_MS`, or we already reloaded for it), else 'idle'.
 * Off (`enabled` false) during a match: nobody reloads mid-game.
 */
export function useBuildUpdate(
  roomId: string,
  peerBuilds: readonly (string | null)[],
  enabled: boolean
): 'idle' | 'checking' | 'downloading' | 'notice' {
  const target = useMemo(
    () => (enabled ? newestPeerBuild(BUILD_ID, peerBuilds) : null),
    [enabled, peerBuilds]
  );
  const [checked, setChecked] = useState<{ target: string; action: 'reload' | 'notice' } | null>(
    null
  );

  useEffect(() => {
    if (target === null) return;
    let live = true;
    void deployedBuild().then((deployed) => {
      if (!live) return;
      const action = updateAction(BUILD_ID, deployed, readAttempt());
      setChecked({ target, action });
      if (action === 'reload' && deployed !== null) {
        writeAttempt(deployed);
        window.location.replace(reloadUrl(window.location.href, roomId, deployed));
      }
    });
    return () => {
      live = false;
    };
  }, [target, roomId]);

  if (target === null) return 'idle';
  if (checked?.target !== target) return 'checking';
  return checked.action === 'reload' ? 'downloading' : 'notice';
}

/** Full-screen notice while the newer build loads. */
export function DownloadingOverlay() {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 px-4">
      <div className="panel title-rise rounded-3xl px-6 py-5 text-center" role="status">
        <p className="text-sm text-fg">Downloading the latest version of territile…</p>
        <p className="mt-1 text-xs text-fg-muted">You'll rejoin this lobby automatically.</p>
      </div>
    </div>
  );
}
